import { useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import PhotoCameraIcon from '@mui/icons-material/PhotoCameraOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useData } from '../store/data';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useFilters } from '../store/filters';
import { fmtInt } from '../lib/format';
import { useT, format } from '../i18n';
import { renderInline } from '../lib/inline-md';

export function SnapshotBanner() {
  const rows = useData(s => s.rows);
  const meta = useData(s => s.meta);
  const filtered = useFilteredRows();
  const f = useFilters();
  const hasFilter = f.hasAnyFilter();
  const [showExportNote, setShowExportNote] = useState(false);
  const t = useT();

  if (!rows.length) {
    return (
      <Alert
        icon={<PhotoCameraIcon />}
        severity="info"
        variant="outlined"
        sx={{ borderRadius: 3 }}
      >
        <AlertTitle>{t.banner.noSnapshotTitle}</AlertTitle>
        {renderInline(t.banner.noSnapshotBody)}
      </Alert>
    );
  }

  const activeFilters: string[] = [];
  if (f.team)     activeFilters.push(`${t.banner.teamPrefix}: ${f.team}`);
  if (f.status)   activeFilters.push(`${t.banner.statusPrefix}: ${f.status}`);
  if (f.assignee) activeFilters.push(`${t.banner.assigneePrefix}: ${f.assignee}`);
  if (f.priority) activeFilters.push(`${t.banner.priorityPrefix}: ${f.priority}`);
  if (f.search)   activeFilters.push(`${t.banner.searchPrefix}: "${f.search}"`);

  return (
    <Alert
      icon={hasFilter ? <FilterAltIcon /> : <PhotoCameraIcon />}
      severity={hasFilter ? 'warning' : 'success'}
      variant="outlined"
      sx={{ borderRadius: 3 }}
    >
      <AlertTitle>
        {hasFilter
          ? format(t.banner.filteredTitle, { filtered: fmtInt(filtered.length), total: fmtInt(rows.length) })
          : t.banner.loadedTitle}
      </AlertTitle>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
        {!hasFilter && <Typography variant="body2">{renderInline(format(t.banner.issues, { count: `**${fmtInt(rows.length)}**` }))}</Typography>}
        {activeFilters.map(label => (
          <Chip key={label} size="small" label={label} variant="filled" color="warning" />
        ))}
        {meta.sourceCount && meta.sourceCount > 1 ? (
          <Chip size="small" label={format(t.banner.mergedCsvs, { count: meta.sourceCount })} variant="outlined" />
        ) : meta.file ? (
          <Chip size="small" label={meta.file} variant="outlined" />
        ) : null}
        {meta.importedAt && (
          <Chip size="small" label={format(t.banner.importedAt, { date: meta.importedAt })} variant="outlined" />
        )}
        <Button
          size="small"
          startIcon={<InfoOutlinedIcon />}
          onClick={() => setShowExportNote(v => !v)}
          sx={{ ml: 'auto' }}
        >
          {showExportNote ? t.banner.hideExportNote : t.banner.showExportNote}
        </Button>
      </Stack>

      <Collapse in={showExportNote} timeout={180}>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            {t.banner.exportConvTitle}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            {renderInline(format(t.banner.exportConvBody, { count: fmtInt(rows.length) }))}
          </Typography>

          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
            {t.banner.exportConvJql}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              fontSize: 12,
              lineHeight: 1.5,
              overflowX: 'auto',
              fontFamily: 'monospace',
            }}
          >{`project = CAL AND "Team Name" = "Door"            ORDER BY created DESC
project = CAL AND "Team Name" = "Window"          ORDER BY created DESC
project = CAL AND "Team Name" = "Sliding"         ORDER BY created DESC
project = CAL AND "Team Name" = "Facade"          ORDER BY created DESC
project = CAL AND "Team Name" = "Stahl"           ORDER BY created DESC
project = CAL AND "Team Name" = "Security Systems" ORDER BY created DESC
project = CAL AND "Team Name" = "France"          ORDER BY created DESC
project = CAL AND "Team Name" = "Team LIXIL"      ORDER BY created DESC`}</Box>

          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
            {renderInline(t.banner.exportConvHint)}
          </Typography>
        </Box>
      </Collapse>
    </Alert>
  );
}
